// User-tunable client preferences. v1 ships language only; future
// additions (theme, default graph) join the same module.
//
// Storage is `localStorage` so all tabs in the same browser share the
// value. Set on the Preferences page; read by `apiFetch` (for the
// Accept-Language header) and any future per-pref consumer.

import { DEFAULT_LABEL_NODE_LIMIT } from './viz/labels';

const LANG_STORAGE_KEY = 'tres.preferred_language';
const THEME_STORAGE_KEY = 'tres.theme';
const VIZ_LABEL_LIMIT_KEY = 'tres.viz.label_node_limit';
const DATABASE_STORAGE_KEY = 'tres.active_database';

/** Fired on `window` when the active database changes, so every open
 *  component re-reads it without threading a prop through the pages. */
export const DATABASE_CHANGED_EVENT = 'tres:active-database-changed';

/**
 * The database every request addresses: the id a site's selector picked,
 * or `null` for the site's default. Read by `apiFetch` for the
 * X-Tres-Database header. A site with one database never sets it.
 */
export function getActiveDatabase(): string | null {
  try {
    const raw = window.localStorage.getItem(DATABASE_STORAGE_KEY);
    if (!raw || !raw.trim()) return null;
    return raw.trim();
  } catch {
    return null;
  }
}

/** Pick the database every request addresses; `null` means the default.
 *  A switch clears every page's remembered graph choice, since a graph
 *  of one database need not exist in another. */
export function setActiveDatabase(id: string | null): void {
  try {
    if (id === null || !id.trim()) {
      window.localStorage.removeItem(DATABASE_STORAGE_KEY);
    } else {
      window.localStorage.setItem(DATABASE_STORAGE_KEY, id.trim());
    }
    clearGraphSelections();
  } catch {
    /* localStorage unavailable; the default database applies. */
  }
  try {
    window.dispatchEvent(new Event(DATABASE_CHANGED_EVENT));
  } catch {
    /* no window event support; the next page load reads the value. */
  }
}

/** Forget every page's remembered graph pick. */
export function clearGraphSelections(): void {
  try {
    const gone: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && (key.startsWith('graphSelector.') || key.startsWith('graphMultiPicker.'))) {
        gone.push(key);
      }
    }
    gone.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* localStorage unavailable; nothing remembered to clear. */
  }
}

/** Theme options. 'system' tracks `prefers-color-scheme`; 'light'/'dark' override. */
export type ThemePreference = 'light' | 'dark' | 'system';

/** Read the persisted theme preference. Defaults to 'system'. */
export function getThemePreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
    return 'system';
  } catch {
    return 'system';
  }
}

/** Persist + apply a theme preference. Pass 'system' to follow the OS. */
export function setThemePreference(theme: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* localStorage unavailable; ignore — applyTheme still runs in memory. */
  }
  applyTheme(theme);
}

/**
 * Resolve a preference to the concrete light/dark mode that should be
 * active right now, consulting `matchMedia` when the preference is
 * 'system'.
 */
export function resolveTheme(theme: ThemePreference): 'light' | 'dark' {
  if (theme === 'light' || theme === 'dark') return theme;
  if (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark';
  }
  return 'light';
}

/**
 * Apply the theme to <html> by adding/removing the `dark` class. Called
 * from main.tsx at boot and from setThemePreference on user change.
 * Tailwind config sets `darkMode: 'class'` so this class is the trigger.
 */
export function applyTheme(theme: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(theme);
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

/**
 * Install the OS-theme listener — when preference is 'system', the
 * resolved theme tracks the user's OS setting in real time. Returns a
 * cleanup function. Call once at SPA boot from main.tsx.
 */
export function watchSystemTheme(): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (getThemePreference() === 'system') applyTheme('system');
  };
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

/**
 * Common base ISO 639-1 codes for the dropdown. Order is roughly
 * "by speaker population, English first." Backend matching is
 * case-insensitive and region-tolerant (en-US → en), so this is the
 * canonical set users see; their typed RDF data may have variants.
 */
export const SUPPORTED_LANGUAGES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'sv', label: 'Svenska' },
  { code: 'pl', label: 'Polski' },
  { code: 'ru', label: 'Русский' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'zh', label: '中文' },
  { code: 'ar', label: 'العربية' },
  { code: 'he', label: 'עברית' },
  { code: 'hi', label: 'हिन्दी' },
];

/**
 * Read the user's preferred-language pref. `null` means "no
 * explicit preference set" — apiFetch will omit the Accept-Language
 * header and the backend falls back to its English-first default.
 */
export function getPreferredLanguage(): string | null {
  try {
    const raw = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (!raw || !raw.trim()) return null;
    return raw.trim().toLowerCase();
  } catch {
    return null;
  }
}

/** Store / clear the preference. Pass `null` to clear. */
export function setPreferredLanguage(code: string | null): void {
  try {
    if (code === null || !code.trim()) {
      window.localStorage.removeItem(LANG_STORAGE_KEY);
    } else {
      window.localStorage.setItem(LANG_STORAGE_KEY, code.trim().toLowerCase());
    }
  } catch {
    // localStorage unavailable (incognito with strict settings); fail
    // silently — the page will surface its own error if the user
    // expected persistence.
  }
}

/**
 * Node count above which the Viz canvas withdraws labels and draws bare
 * boxes (0 = never for count; the zoom floor still applies). Read on every
 * relayout, so a change on the Preferences page takes effect without reload.
 */
export function getVizLabelLimit(): number {
  try {
    const raw = window.localStorage.getItem(VIZ_LABEL_LIMIT_KEY);
    if (raw === null) return DEFAULT_LABEL_NODE_LIMIT;
    const n = Number(raw);
    return Number.isInteger(n) && n >= 0 ? n : DEFAULT_LABEL_NODE_LIMIT;
  } catch {
    return DEFAULT_LABEL_NODE_LIMIT;
  }
}

export function setVizLabelLimit(limit: number): void {
  try {
    window.localStorage.setItem(VIZ_LABEL_LIMIT_KEY, String(Math.max(0, Math.floor(limit))));
  } catch {
    /* localStorage unavailable; the default applies. */
  }
}
